import { type WritableDraft } from 'immer';

import {
  type PartialPropsOf,
  type SlotAlignment,
  type SlotData,
  type SlotSizing,
} from '@blaze-runtime/engine-domain';
import {
  type ElementAlignment,
  type ElementVerticalAlignment,
} from '@blaze-shared/elements-domain';

import { type EngineState } from '../../../+state/engine';
import { HorizontalAlignmentStyle, VerticalAlignmentStyle } from '../../../ai/models/styles';
import { type AiChildGroup, type ElementHandler } from '../../../ai/page/element-handler';
import { getInitialProps } from '../../../ai/page/prop-builders';
import { getElement, upsertElement } from '../../../ai/page/utils';

import {
  type ContainerContext,
  type ContainerDefinition,
  type ContainerSlot,
} from './container.definition';

export class ContainerElementHandler implements ElementHandler<'container'> {
  readonly type = 'container';

  getChildGroups(element: ContainerDefinition): AiChildGroup[] {
    return (element.children ?? []).map((slot, index) => ({
      slotId: `${index}`,
      children: slot.children,
    }));
  }

  process(context: ContainerContext, draft: WritableDraft<EngineState>): void {
    const props: PartialPropsOf<'container'> = getInitialProps<'container'>(
      draft,
      context.element.id,
      { content: {} },
    );

    if (context.element.styleName) {
      props.styleConfig = {
        styleName: context.element.styleName,
      };
    }

    const { children } = context.element;

    if (!Array.isArray(children)) {
      return;
    }

    const result = upsertElement(draft, context, props);
    const element = getElement(draft, result.elementId, 'container');

    if (context.element.minimumHeight !== undefined) {
      element.height = {
        mode: 'value',
        value: context.element.minimumHeight,
        fixed: false,
      };
    }

    element.slots.ids = [];

    const sizings = this.getSlotsSizing(children);
    for (let index = 0; index < children.length; index++) {
      const slot = children[index];

      element.slots.ids.push(`${index}`);

      const elementSlot: SlotData<PartialPropsOf<'container'>> = {
        children: [],
        alignment: this.getSlotAlignment(slot),
        sizing: sizings[index],
        props: {},
      };

      if (slot.styleName) {
        elementSlot.props = {
          styleConfig: {
            styleName: slot.styleName,
          },
        };
      }
      element.slots[`${index}`] = elementSlot;
    }
  }

  private getSlotsSizing(slots: ContainerSlot[]): SlotSizing[] {
    const electedFillIndex = this.getElectedFillIndex(slots);

    const explicitSum = slots.reduce<number>(
      (sum, slot, index) =>
        index === electedFillIndex ? sum : sum + (this.getExplicitWidth(slot) ?? 0),
      0,
    );

    return slots.map((slot, index) => {
      if ((slot.widthMode ?? 'Percent') === 'FitContent') {
        return { mode: 'fit-content' };
      }

      if (index === electedFillIndex || this.isUnsizedColumn(slot)) {
        return { mode: 'auto' };
      }

      const width = slot.widthPercent ?? 0;
      const normalized = explicitSum > 1 ? width / explicitSum : width;
      return { mode: 'percent', width: normalized * 100 };
    });
  }

  private getElectedFillIndex(slots: ContainerSlot[]): number | undefined {
    if (slots.length < 2 || slots.some((slot) => this.isUnsizedColumn(slot))) {
      return undefined;
    }

    let electedIndex: number | undefined;
    let widestWidth = -Infinity;

    slots.forEach((slot, index) => {
      const width = this.getExplicitWidth(slot);
      if (width !== undefined && width >= widestWidth) {
        widestWidth = width;
        electedIndex = index;
      }
    });

    return electedIndex;
  }

  private isUnsizedColumn(slot: ContainerSlot): boolean {
    return (slot.widthMode ?? 'Percent') !== 'FitContent' && slot.widthPercent === undefined;
  }

  private getExplicitWidth(slot: ContainerSlot): number | undefined {
    if ((slot.widthMode ?? 'Percent') === 'FitContent' || slot.widthPercent === undefined) {
      return undefined;
    }

    return slot.widthPercent;
  }

  private getRowAlign(element: ContainerDefinition): ElementAlignment | undefined {
    if (!element.verticalAlignment && !element.horizontalAlignment) {
      return undefined;
    }

    const vertical = getVerticalAlignment(element.verticalAlignment) ?? 'top';
    const horizontal = getHorizontalAlignment(element.horizontalAlignment) ?? 'left';
    return `${vertical} ${horizontal}`;
  }

  private getSlotAlignment(slot: ContainerSlot): SlotAlignment | undefined {
    if (!slot.verticalAlignment && !slot.horizontalAlignment) {
      return undefined;
    }

    const alignment: SlotAlignment = {
      syncVerticalLineAlignment: true,
      horizontal: getHorizontalAlignment(slot.horizontalAlignment) ?? 'left',
      vertical: getVerticalAlignment(slot.verticalAlignment) ?? 'top',
    };

    return alignment;
  }
}

function getVerticalAlignment(
  verticalAlignment: VerticalAlignmentStyle | undefined,
): ElementVerticalAlignment | undefined {
  switch (verticalAlignment) {
    case VerticalAlignmentStyle.Top:
      return 'top';
    case VerticalAlignmentStyle.Center:
      return 'middle';
    case VerticalAlignmentStyle.Bottom:
      return 'bottom';
  }

  return undefined;
}

function getHorizontalAlignment(
  horizontalAlignment: HorizontalAlignmentStyle | undefined,
): 'left' | 'center' | 'right' | undefined {
  switch (horizontalAlignment) {
    case HorizontalAlignmentStyle.Left:
      return 'left';
    case HorizontalAlignmentStyle.Center:
      return 'center';
    case HorizontalAlignmentStyle.Right:
      return 'right';
  }

  return undefined;
}
